import * as THREE from 'three';

export interface RevealController {
  setRadius: (r: number) => void;
  setCenter: (x: number, y: number, z: number) => void;
  setSoftness: (s: number) => void;
  dispose: () => void;
}

/**
 * Installs a radial sphere-wavefront reveal effect on mkkellogg's splat
 * ShaderMaterial. Splats inside the sphere render normally; splats outside
 * fade out via a smoothstep band of width `softness`.
 *
 * Animate radius from below-scene to past-scene-extent to drive a "wavefront
 * expanding from origin" load-in. Spark had this via dyno shader blocks; here
 * we patch the material's GLSL directly since mkkellogg doesn't expose a
 * modifier system.
 */
export function installRevealEffect(
  material: THREE.ShaderMaterial,
  initialSoftness = 60
): RevealController {
  const uniforms = material.uniforms as Record<string, { value: unknown }>;
  uniforms['uRevealRadius'] = { value: -1e6 };
  uniforms['uRevealCenter'] = { value: new THREE.Vector3(0, 0, 0) };
  uniforms['uRevealSoftness'] = { value: initialSoftness };

  // Both injections anchor on `void main` which is present in both shaders
  // regardless of variant. Regex-anchoring on `precision` is unreliable because
  // mkkellogg indents the precision line (leading whitespace) so `^precision`
  // with /m flag doesn't match — we hit this and silently lost the fragment
  // varying declaration, causing a vert/frag varying mismatch and a program
  // link failure ("useProgram: program not valid").

  // Vertex: declare uniforms + varying, then compute reveal after splatCenter
  // is assigned. splatCenter is the world-space splat position.
  if (!material.vertexShader.includes('uRevealRadius')) {
    material.vertexShader = material.vertexShader
      .replace(
        /(void\s+main\s*\()/,
        `
uniform float uRevealRadius;
uniform vec3 uRevealCenter;
uniform float uRevealSoftness;
varying float vReveal;

$1`
      )
      .replace(
        /(splatCenter\s*=[^;]+;)/,
        `$1
{
  float d = distance(splatCenter, uRevealCenter);
  vReveal = 1.0 - smoothstep(uRevealRadius - uRevealSoftness, uRevealRadius, d);
}
`
      );
  }

  // Fragment: declare matching varying, then modulate gl_FragColor alpha
  // *after* the existing assignment. Form-agnostic — works across mkkellogg's
  // splat fragment shader variants.
  if (!material.fragmentShader.includes('vReveal')) {
    material.fragmentShader = material.fragmentShader
      .replace(
        /(void\s+main\s*\()/,
        `
varying float vReveal;

$1`
      )
      .replace(
        /(gl_FragColor\s*=[^;]+;)/,
        `$1
if (vReveal < 0.001) discard;
gl_FragColor.a *= vReveal;`
      );
  }

  material.needsUpdate = true;

  const centerVec = uniforms['uRevealCenter']!.value as THREE.Vector3;

  return {
    setRadius: (r) => {
      uniforms['uRevealRadius']!.value = r;
    },
    setCenter: (x, y, z) => {
      centerVec.set(x, y, z);
    },
    setSoftness: (s) => {
      uniforms['uRevealSoftness']!.value = s;
    },
    dispose: () => {
      // Restore to "fully revealed" before detaching so subsequent renders
      // (e.g. if a parent decides not to call dispose for a frame) don't flash.
      uniforms['uRevealRadius']!.value = 1e6;
    }
  };
}
